import React, { useEffect, useState } from 'react';
import { FileUpload, Input, Dropdown } from '../forms';
import { ExtendedCreateProjectData, ProjectDrawing } from '@/types';
import { FileImage, Trash2, Edit } from 'lucide-react';

interface DrawingsStepProps {
  data: Partial<ExtendedCreateProjectData>;
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

const DrawingsStep: React.FC<DrawingsStepProps> = ({ data, updateData, setValid }) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [editingDrawing, setEditingDrawing] = useState<number | null>(null);

  const drawingCategories = [
    { value: 'architectural', label: 'Architectural' },
    { value: 'structural', label: 'Structural' },
    { value: 'mechanical', label: 'Mechanical' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'site-plan', label: 'Site Plan' },
    { value: 'elevations', label: 'Elevations' },
    { value: 'sections', label: 'Sections' },
    { value: 'details', label: 'Details' },
    { value: 'other', label: 'Other' },
  ];

  const validateForm = () => {
    // Always valid - drawings are optional
    setValid(true);
    return true;
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.drawings]);

  const handleFilesChange = (files: File[]) => {
    setUploadedFiles(files);

    // Create drawing objects for new files
    const newDrawings: ProjectDrawing[] = files.map(file => ({
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
      file: file,
      category: 'other',
      version: '1.0',
    }));

    // Update existing drawings with new files
    const existingDrawings = data.drawings || [];
    const updatedDrawings = [
      ...existingDrawings.filter(
        drawing =>
          typeof drawing.file === 'string' ||
          files.some(file => file.name === (drawing.file as File).name)
      ),
      ...newDrawings.filter(
        newDrawing =>
          !existingDrawings.some(
            existing =>
              existing.file instanceof File && existing.file.name === (newDrawing.file as File).name
          )
      ),
    ];

    updateData({ drawings: updatedDrawings });
  };

  const updateDrawing = (index: number, updates: Partial<ProjectDrawing>) => {
    const updatedDrawings = [...(data.drawings || [])];
    updatedDrawings[index] = { ...updatedDrawings[index], ...updates };
    updateData({ drawings: updatedDrawings });
  };

  const removeDrawing = (index: number) => {
    const updatedDrawings = [...(data.drawings || [])];
    const removedDrawing = updatedDrawings.splice(index, 1)[0];

    // Remove from uploaded files if it's a File object
    if (removedDrawing.file instanceof File) {
      setUploadedFiles(prev => prev.filter(file => file !== removedDrawing.file));
    }

    updateData({ drawings: updatedDrawings });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileSize = (file: File | string) => {
    if (file instanceof File) {
      return formatFileSize(file.size);
    }
    return 'Unknown size';
  };

  const drawingsByCategory = drawingCategories.map(category => ({
    ...category,
    drawings: (data.drawings || []).filter(drawing => drawing.category === category.value),
  }));

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-brand-800 mb-2">Project Drawings</h4>
        <p className="text-sm text-brand-primary">
          Upload architectural drawings, plans, and technical documents. Organize them by category
          for easy access.
        </p>
      </div>

      {/* File Upload */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Upload Drawings</h4>
        <FileUpload
          label="Drawing Files"
          accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.tiff,.bmp"
          multiple={true}
          maxSize={50}
          maxFiles={20}
          files={uploadedFiles}
          onChange={handleFilesChange}
          hint="Supported formats: PDF, DWG, DXF, JPG, PNG, TIFF • Max 50MB per file"
        />
      </div>

      {/* Drawings Management */}
      {data.drawings && data.drawings.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Manage Drawings</h4>

          <div className="space-y-4">
            {(data.drawings || []).map((drawing, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-4">
                  <FileImage className="h-8 w-8 text-brand-primary mt-1" />

                  <div className="flex-1 space-y-3">
                    {editingDrawing === index ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Input
                          label="Drawing Name"
                          value={drawing.name}
                          onChange={e => updateDrawing(index, { name: e.target.value })}
                          placeholder="Enter drawing name"
                        />
                        <Dropdown
                          label="Category"
                          options={drawingCategories}
                          value={drawing.category}
                          onChange={value => updateDrawing(index, { category: value })}
                        />
                        <Input
                          label="Version"
                          value={drawing.version}
                          onChange={e => updateDrawing(index, { version: e.target.value })}
                          placeholder="1.0"
                        />
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-900">{drawing.name}</h5>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingDrawing(index)}
                              className="p-1 text-gray-400 hover:text-brand-primary transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => removeDrawing(index)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span className="px-2 py-1 bg-brand-100 text-brand-800 rounded text-xs">
                            {drawingCategories.find(cat => cat.value === drawing.category)?.label ||
                              drawing.category}
                          </span>
                          <span>Version {drawing.version}</span>
                          <span>{getFileSize(drawing.file)}</span>
                          {drawing.file instanceof File && (
                            <span className="text-green-600">New upload</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {editingDrawing === index && (
                  <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setEditingDrawing(null)}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setEditingDrawing(null)}
                      className="px-3 py-1 text-sm bg-brand-primary text-white rounded hover:bg-brand-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drawings by Category Summary */}
      {data.drawings && data.drawings.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-green-800 mb-3">Drawings by Category</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drawingsByCategory
              .filter(category => category.drawings.length > 0)
              .map(category => (
                <div key={category.value} className="bg-white rounded border border-green-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-green-900">{category.label}</h5>
                    <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                      {category.drawings.length}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {category.drawings.slice(0, 3).map((drawing, index) => (
                      <p key={index} className="text-xs text-green-700 truncate">
                        {drawing.name} v{drawing.version}
                      </p>
                    ))}
                    {category.drawings.length > 3 && (
                      <p className="text-xs text-green-600">+{category.drawings.length - 3} more</p>
                    )}
                  </div>
                </div>
              ))}
          </div>
          <p className="text-xs text-green-600 mt-3">
            Total: {data.drawings.length} drawings uploaded
          </p>
        </div>
      )}

      {/* Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">Tips for Drawing Management</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Use descriptive names for easy identification</li>
          <li>• Organize drawings by category for better navigation</li>
          <li>• Keep version numbers updated when uploading revisions</li>
          <li>• Supported formats ensure compatibility across teams</li>
        </ul>
      </div>
    </div>
  );
};

export default DrawingsStep;
